import { stringify } from 'query-string';
import { HttpClient } from 'api/http-client';
import { User } from 'api/models';
import { ProjectInUserProfile } from 'api/user';
import { Filter } from 'components/molecules/filters/types';
import { TemplateObjectType } from 'data/templates/api';
import { ApiFilter } from 'utils/simple-search-filter/simple-search-filter';

interface BaseRating {
  rating: number;
}

export interface Rating extends BaseRating {
  count: number;
}

export interface TemplateRating$Api extends Rating {
  projectTemplateId: number;
  sourceId: number;
}

export interface Template {
  objectType: TemplateObjectType;
  templateId: number;
  templateName?: string;
}

export interface TemplateRating
  extends Omit<TemplateRating$Api, 'projectTemplateId'> {
  projectTemplate: Template;
}

export interface ProjectManagerRating$Api extends Rating {
  projectManager: number;
}

export interface ProjectManagerRating
  extends Omit<ProjectManagerRating$Api, 'projectManager'> {
  projectManager: User;
}

export enum CsatAction {
  CsatSCore = 'CSAT_SCORE',
}

export interface CsatRating$Api extends BaseRating {
  actionBy: number;
  actionAt: number;
  action: CsatAction;
  projectId: number;
  comment?: string;
  projectManager: number;
  taskId: number;
  accountId: number;
  customerId: number;
  projectTemplateId?: number;
  taskTemplateId?: number;
  taskName?: string;
}

export interface CsatRating
  extends Omit<
    CsatRating$Api,
    'actionBy' | 'projectId' | 'projectTemplateId' | 'taskTemplateId'
  > {
  actionBy: User;
  project: ProjectInUserProfile;
  taskTemplate?: Template;
  projectTemplate?: Template;
}

export enum ExportStatus {
  Completed = 'COMPLETED',
}

export interface Export {
  exportsMetaDataId: number;
  status: ExportStatus;
  downloadUrl: string;
  condition: string;
  requestedBy: {
    userId: number;
    identityType: 'USER';
  };
  requestedAt: number;
  createdAt: number;
}

export const syntheticResponse = <T = any>(value: T, delay = 500): Promise<T> =>
  new Promise((resolve) => {
    const wait = window.setTimeout(() => {
      clearTimeout(wait);
      resolve(value);
    }, delay);
  });

export interface Params {
  filter?: string;
}

export interface FilterParams {
  filter: Filter;
  startDate: string;
  endDate: string;
}

class CsatHttpClient extends HttpClient {
  public getOverallRating = async ({
    filter,
    endDate,
    startDate,
  }: FilterParams): Promise<Rating[]> => {
    const config = {
      params: {
        group: 'rating',
        aggregate: 'count(rating)',
        startDate,
        endDate,
        type: filter.type,
      },
    };

    return this.get(
      `reports/csat-data?${ApiFilter.toQueryStringFormat(
        filter.criteria,
      )}&${stringify(config.params)}`,
    );
  };

  public getAvgTemplateRatings = async ({
    filter,
    endDate,
    startDate,
  }: FilterParams): Promise<TemplateRating$Api[]> => {
    const config = {
      params: {
        group: 'projectTemplateId,rating',
        aggregate: 'count(rating)',
        endDate,
        startDate,
        type: filter.type,
      },
    };

    return this.get(
      `reports/csat-data?${ApiFilter.toQueryStringFormat(
        filter.criteria,
      )}&${stringify(config.params)}`,
    );
  };

  public getAvgProjectManagerRatings = async ({
    filter,
    endDate,
    startDate,
  }: FilterParams): Promise<ProjectManagerRating$Api[]> => {
    const config = {
      params: {
        group: 'projectManager',
        aggregate: 'avg(rating),count(rating)',
        endDate,
        startDate,
        type: filter.type,
      },
    };

    return this.get(
      `reports/csat-data?${ApiFilter.toQueryStringFormat(
        filter.criteria,
      )}&${stringify(config.params)}`,
    );
  };

  public getAllData = async ({ filter }: Params = {}): Promise<
    CsatRating$Api[]
  > => {
    const config = {
      params: {
        filter,
      },
    };

    return this.get('/reports/csat', config);
  };

  public exportData = async ({ filter }: Params = {}): Promise<Export> => {
    const config = {
      params: {
        filter,
      },
    };

    return this.get('/reports/export-data', config);
  };

  public async getCsatData({ filter, endDate, startDate }: FilterParams) {
    return this.get<CsatRating$Api[]>(
      `/reports/csat-data?${ApiFilter.toQueryStringFormat(
        filter.criteria,
      )}&${stringify({ startDate, endDate, type: filter.type })}`,
    );
  }

  public async exportCsatData({ filter, endDate, startDate }: FilterParams) {
    return this.get<Export>(
      `/reports/csat-data/export?${ApiFilter.toQueryStringFormat(
        filter.criteria,
      )}&${stringify({ endDate, startDate, type: filter.type })}`,
    );
  }
}

export const CsatAPI = new CsatHttpClient();
